import * as React from "react";
import { Loader2, Settings2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "../../../core/lib/supabase";
import type { TemplateBlock } from "../../../features/blocks/types/blockTypes";
import type { SelectedTemplateWithConfig } from "../../../features/projects/create-project-wizard/wizardTypes";
import {
  extractFieldsViaOrganizationBackend,
  getTemplateBlockSlotsViaOrganizationBackend,
} from "../../../features/projects/services/organizationBackendExtended";

interface TemplateConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string | null;
  template: SelectedTemplateWithConfig | null /** catalog template row */;
  onSave: (next: SelectedTemplateWithConfig) => void;
}

export const TemplateConfigDialog: React.FC<TemplateConfigDialogProps> = ({
  open,
  onOpenChange,
  organizationId,
  template,
  onSave,
}) => {
  const [step, setStep] = React.useState<"variables" | "blocks">("variables");
  const [fieldNames, setFieldNames] = React.useState<string[]>([]);
  const [fieldValues, setFieldValues] = React.useState<Record<string, string>>({});
  const [slotCount, setSlotCount] = React.useState(4);
  const [loadingFields, setLoadingFields] = React.useState(false);
  const [loadingSlots, setLoadingSlots] = React.useState(false);
  const [libraryBlocks, setLibraryBlocks] = React.useState<TemplateBlock[]>([]);
  const [selectedBlockIds, setSelectedBlockIds] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    if (!open || !template) {
      return () => {};
    }
    setStep("variables");
    setFieldValues({ ...template.fieldValues });
    const ids = new Set(template.blocks.map((b) => b.id));
    setSelectedBlockIds(ids);
    setSlotCount(template.slotCount || 4);

    let cancelled = false;
    (async () => {
      setLoadingFields(true);
      try {
        const res = await extractFieldsViaOrganizationBackend(template.id, { from_latest: false });
        const names = res.fields ?? [];
        if (!cancelled) setFieldNames(names);
      } catch {
        if (!cancelled) setFieldNames([]);
      } finally {
        if (!cancelled) setLoadingFields(false);
      }
    })();

    (async () => {
      setLoadingSlots(true);
      try {
        const { slotCount: sc } = await getTemplateBlockSlotsViaOrganizationBackend(template.id);
        if (!cancelled) setSlotCount(sc);
      } catch {
        if (!cancelled) setSlotCount(4);
      } finally {
        if (!cancelled) setLoadingSlots(false);
      }
    })();

    (async () => {
      if (!organizationId) {
        setLibraryBlocks([]);
        return;
      }
      const { data, error } = await supabase
        .from("template_blocks")
        .select("*")
        .eq("organization_id", organizationId)
        .order("title", { ascending: true });
      if (!cancelled && !error) {
        setLibraryBlocks((data || []) as TemplateBlock[]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, template?.id, organizationId]);

  const toggleBlock = (id: string) => {
    setSelectedBlockIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = () => {
    if (!template) return;
    const blocks = libraryBlocks.filter((b) => selectedBlockIds.has(b.id));
    onSave({
      ...template,
      fieldValues,
      blocks,
      slotCount,
    });
    onOpenChange(false);
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[400px] max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b shrink-0">
          <DialogTitle className="text-sm font-semibold flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            {template.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 px-3 pt-2 border-b shrink-0">
          <button
            type="button"
            className={`px-3 py-1.5 text-xs rounded-md font-medium ${
              step === "variables" ? "bg-primary/10 text-primary" : "text-muted-foreground"
            }`}
            onClick={() => setStep("variables")}
          >
            Variables
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 text-xs rounded-md font-medium ${
              step === "blocks" ? "bg-primary/10 text-primary" : "text-muted-foreground"
            }`}
            onClick={() => setStep("blocks")}
          >
            Blocks
          </button>
        </div>

        <ScrollArea className="flex-1 min-h-[220px] max-h-[340px]">
          <div className="p-4 space-y-3">
            {step === "variables" && (
              <>
                {loadingFields ? (
                  <div className="flex items-center gap-2 text-muted-foreground text-xs">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading fields…
                  </div>
                ) : fieldNames.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No merge fields detected for this template.</p>
                ) : (
                  fieldNames.map((name) => (
                    <div key={name} className="space-y-1">
                      <Label className="text-xs">{name}</Label>
                      <Input
                        className="h-8 text-xs"
                        value={fieldValues[name] ?? ""}
                        onChange={(e) =>
                          setFieldValues((prev) => ({
                            ...prev,
                            [name]: e.target.value,
                          }))
                        }
                      />
                    </div>
                  ))
                )}
              </>
            )}

            {step === "blocks" && (
              <>
                <p className="text-xs text-muted-foreground">
                  Slot count: {loadingSlots ? "…" : slotCount}
                </p>
                {!organizationId ? (
                  <p className="text-xs text-amber-700">Organization not loaded — block library unavailable.</p>
                ) : libraryBlocks.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No blocks in library for this organization.</p>
                ) : (
                  <div className="space-y-1">
                    {libraryBlocks.map((b) => (
                      <label
                        key={b.id}
                        className="flex items-start gap-2 text-xs cursor-pointer rounded border border-border p-2 hover:bg-muted/40"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={selectedBlockIds.has(b.id)}
                          onChange={() => toggleBlock(b.id)}
                        />
                        <span className="font-medium text-foreground">{b.title}</span>
                      </label>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="px-3 py-2 border-t shrink-0 gap-2">
          <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" className="text-xs h-8" onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
